<template>
  <div class="side-navigation-container">
    <!-- Toggle Button for Small Screens -->
    <button 
      class="side-nav-toggle d-lg-none"
      @click="toggleSidebar"
      aria-label="Toggle navigation"
    >
      <i class="bi" :class="isOpen ? 'bi-x-lg' : 'bi-list'"></i>
      <span>{{ isOpen ? 'Close' : 'Contents' }}</span>
    </button>
    
    <!-- Sidebar Content -->
    <aside :class="['side-navigation', { 'is-open': isOpen }]">
      <div class="side-nav-header">
        <h3 class="side-nav-title">Contents</h3>
        <button 
          class="close-btn d-lg-none"
          @click="toggleSidebar"
          aria-label="Close navigation"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      
      <nav class="side-nav-content">
        <ul class="side-nav-sections">
          <li 
            v-for="(section, index) in sections" 
            :key="`section-${index}-${section.ref}`"
            :class="['side-nav-section', { active: activeSection === section.ref }]"
          >
            <a 
              :href="`#${section.ref}`"
              @click="scrollToSection(section.ref)"
              class="section-link"
            >
              {{ section.title }}
            </a>
            
            <ul v-if="section.subsections" class="side-nav-subsections">
              <li 
                v-for="(sub, subIndex) in section.subsections" 
                :key="`subsection-${subIndex}-${sub.ref}`"
                :class="['side-nav-subsection', { active: activeSection === sub.ref }]"
              >
                <a 
                  :href="`#${sub.ref}`"
                  @click="scrollToSection(sub.ref)"
                  class="subsection-link"
                >
                  {{ sub.title }}
                </a>
                
                <!-- Third level for nested subsections -->
                <ul v-if="sub.subsections" class="side-nav-subsubsections">
                  <li 
                    v-for="(subSub, subSubIndex) in sub.subsections" 
                    :key="`subsubsection-${subSubIndex}-${subSub.ref}`"
                    :class="['side-nav-subsubsection', { active: activeSection === subSub.ref }]"
                  >
                    <a 
                      :href="`#${subSub.ref}`"
                      @click="scrollToSection(subSub.ref)"
                      class="subsubsection-link"
                    >
                      {{ subSub.title }}
                    </a>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </nav>
      
      <div class="side-nav-footer">
        <router-link to="/" class="back-to-home">
          <i class="bi bi-house-fill"></i> Back to Home
        </router-link>
      </div>
    </aside>
    
    <!-- Overlay for Small Screens -->
    <div 
      class="side-nav-overlay d-lg-none" 
      :class="{ 'is-visible': isOpen }" 
      @click="closeSidebar"
    ></div>
  </div>
</template>

<script>
import { watch, ref, onMounted, onBeforeUnmount } from 'vue';

export default {
  name: 'SideNavigation',
  props: {
    sections: {
      type: Array,
      required: true,
    }
  },
  setup(props) {
    const isOpen = ref(false);
    const activeSection = ref('');
    const scrollTimeout = ref(null);

    const toggleSidebar = () => {
      isOpen.value = !isOpen.value;
      
      // Prevent scrolling when sidebar is open on mobile
      if (isOpen.value) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    };

    const closeSidebar = () => {
      isOpen.value = false;
      document.body.style.overflow = '';
    };

    const scrollToSection = (sectionRef) => {
      // Close the sidebar on mobile after a click
      if (window.innerWidth < 992) {
        closeSidebar();
      }
      
      const element = document.getElementById(sectionRef);
      if (element) {
        // Add offset for the fixed header
        const headerHeight = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };

    const onScroll = () => {
      // Throttle scroll event
      if (scrollTimeout.value) {
        clearTimeout(scrollTimeout.value);
      }
      
      scrollTimeout.value = setTimeout(() => {
        checkActiveSection();
      }, 100);
    };

    const checkActiveSection = () => {
      // Find all sections and subsections with IDs
      const allSections = [];
      
      props.sections.forEach(section => {
        if (section.ref) {
          allSections.push(section.ref);
        }
        
        if (section.subsections) {
          section.subsections.forEach(sub => {
            if (sub.ref) {
              allSections.push(sub.ref);
            }
            
            if (sub.subsections) {
              sub.subsections.forEach(subSub => {
                if (subSub.ref) {
                  allSections.push(subSub.ref);
                }
              });
            }
          });
        }
      });
      
      // Check which section is currently most visible
      let mostVisibleSection = '';
      let maxVisibility = 0;
      const headerHeight = 80;
      
      allSections.forEach(sectionRef => {
        const element = document.getElementById(sectionRef);
        if (element) {
          const rect = element.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          
          // Calculate how much of the element is visible
          const visibleTop = Math.max(rect.top, headerHeight);
          const visibleBottom = Math.min(rect.bottom, viewportHeight);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          
          // Update most visible section
          if (visibleHeight > maxVisibility) {
            maxVisibility = visibleHeight;
            mostVisibleSection = sectionRef;
          }
        }
      });
      
      activeSection.value = mostVisibleSection;
    };

    // Wenn sich die Abschnitte ändern, aktualisieren wir den aktiven Abschnitt
    watch(() => props.sections, () => {
      checkActiveSection();
    }, { deep: true });

    onMounted(() => {
      window.addEventListener('scroll', onScroll);
      checkActiveSection();
    });
    
    onBeforeUnmount(() => {
      window.removeEventListener('scroll', onScroll);
    });

    return {
      isOpen,
      activeSection,
      toggleSidebar,
      closeSidebar,
      scrollToSection
    };
  }
};
</script>

<style scoped>
/* CSS bleibt unverändert */
.side-navigation-container {
  position: relative;
  z-index: 1020;
}

.side-navigation {
  position: fixed;
  top: 80px; /* Header height */
  left: 0;
  width: 270px;
  height: calc(100vh - 80px);
  background-color: white;
  border-right: 1px solid #eee;
  overflow-y: auto;
  z-index: 1020;
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  margin-top: 0; /* No top margin, starts exactly at 80px from top */
}

.side-nav-header {
  padding: 1.5rem 1rem;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f2f5e7;
}

.side-nav-title {
  margin: 0;
  color: #333;
  font-size: 1.3rem;
  font-weight: 600;
}

.side-nav-content {
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
}

.side-nav-footer {
  padding: 1rem;
  border-top: 1px solid #eee;
  background-color: white;
}

.side-nav-sections {
  list-style: none;
  padding: 0;
  margin: 0;
}

.side-nav-section {
  margin-bottom: 0.5rem;
}

.section-link {
  display: block;
  padding: 0.5rem 1rem;
  color: #333;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

.section-link:hover, .section-link:focus {
  color: #87B140;
  background-color: #f2f5e7;
  border-left-color: #87B140;
}

.side-nav-section.active > .section-link {
  color: #87B140;
  background-color: #f2f5e7;
  border-left-color: #87B140;
  font-weight: 600;
}

.side-nav-subsections {
  list-style: none;
  padding-left: 1rem;
  margin-top: 0.25rem;
}

.subsection-link {
  display: block;
  padding: 0.4rem 1rem;
  color: #555;
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  border-left: 2px solid transparent;
}

.subsection-link:hover, .subsection-link:focus {
  color: #87B140;
  background-color: #f2f5e7;
  border-left-color: #87B140;
}

.side-nav-subsection.active > .subsection-link {
  color: #87B140;
  background-color: #f2f5e7;
  border-left-color: #87B140;
  font-weight: 600;
}

.side-nav-subsubsections {
  list-style: none;
  padding-left: 1rem;
  margin-top: 0.25rem;
}

.subsubsection-link {
  display: block;
  padding: 0.3rem 1rem;
  color: #666;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-left: 1px solid transparent;
}

.subsubsection-link:hover, .subsubsection-link:focus {
  color: #87B140;
  background-color: #f2f5e7;
  border-left-color: #87B140;
}

.side-nav-subsubsection.active > .subsubsection-link {
  color: #87B140;
  background-color: #f2f5e7;
  border-left-color: #87B140;
  font-weight: 600;
}

.back-to-home {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #555;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  padding: 0.5rem;
}

.back-to-home:hover, .back-to-home:focus {
  color: #87B140;
}

/* Mobile Toggle Button */
.side-nav-toggle {
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 1025;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #87B140;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 30px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
}

.side-nav-toggle:hover {
  background-color: #75a236;
  transform: translateY(-2px);
}

.close-btn {
  background: none;
  border: none;
  color: #555;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0;
}

.close-btn:hover {
  color: #87B140;
}

/* Overlay for Mobile */
.side-nav-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1015;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

.side-nav-overlay.is-visible {
  opacity: 1;
  visibility: visible;
}

/* Responsive Adjustments */
@media (max-width: 991.98px) {
  .side-navigation {
    transform: translateX(-100%);
    width: 280px;
    top: 0;
    height: 100vh;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  }
  
  .side-navigation.is-open {
    transform: translateX(0);
  }
}
</style>